import { DEVICES } from '@/constants/medconnect-devices-types'
import { handleBluetooth, handleSerial } from './communications'
import { Labels, Mesure } from './constant'



export type DeviceProps = {
  device: DEVICES
  baudRate?: number
  emitData: (mesure: Mesure) => void
  onError: (error: string) => void
  onDisconnect: () => void
  onConnect: () => void
}

export function selectDevice(prop: DeviceProps) {
  switch (prop.device) {
    case DEVICES.PC303:
      return PC303(prop)
    case DEVICES.PM6100:
      return PM6100(prop)
    case DEVICES.THERMO_TAIDOC:
      return THERMO_TAIDOC(prop)
    case DEVICES.TAIDOC_BGMETER:
      return TAIDOC_BGMETER(prop)
    case DEVICES.F4:
      return F4(prop)
    default:
      return
  }
}

function PC303(prop: DeviceProps) {
  var dataString = ''

  const handlehexData = (data: string) => {
    dataString += data
    if (dataString.length >= 18) {
      // Search SPO2 and Pouls
      // Exemple: [aa 55 53 07 01 61 43 00 25 00 d9] corresponds à SpO2 = 97% et Pouls = 67 bpm
      if (dataString.substring(0, dataString.length - 6).includes('aa55530701')) {
        const handleSPO2 = (data: string) => {
          const n = data.search('aa55530701')
          const valSPO2 = parseInt(data.substring(n + 10, n + 12), 16)
          const valPouls = parseInt(data.substring(n + 12, n + 14), 16)
          prop.emitData({
            label: Labels.BPM,
            value: valPouls.toString(),
            unit: 'bpm',
          })
          prop.emitData({
            label: Labels.SPO2,
            value: valSPO2.toString(),
            unit: '%',
          })
        }
        handleSPO2(dataString)
        dataString = ''
      }
      // Search Tension
      // Exemple: [aa 55 43 07 01 00 8d 00 60 00 0e] corresponds à une tension de 141/90
      if (dataString.substring(0, dataString.length - 6).includes('aa5543070100')) {
        const handleTension = (data: string) => {
          const n = data.search('aa5543070100')
          const tmpHigh = parseInt(data.substring(n + 12, n + 14), 16)
          const tmpLo = parseInt(data.substring(n + 16, n + 18), 16)
          prop.emitData({
            label: Labels.NIBP,
            value: `${(tmpHigh / 10).toString()}/${(tmpLo / 10).toString()}`,
            unit: '%',
          })
        }
        handleTension(dataString)
        dataString = ''
      }
      // Search Temperature
      // Exemple: [aa 55 72 05 01 40 02 61 53] corresponds à une température de 36,09 Celsius, (30 + 609 / 100)
      if (dataString.substring(0, dataString.length - 6).includes('aa5572050140')) {
        const handleTemp = (data: string) => {
          const n = data.search('aa5572050140')
          const tmpRead = parseInt(data.substring(n + 12, n + 16), 16)
          prop.emitData({
            label: Labels.TEMP,
            value: (Math.round((30 + tmpRead / 100) * 10) / 10).toString(),
            unit: '°C',
          })
        }
        handleTemp(dataString)
        dataString = ''
      }
      // Search Gycemie
      // Exemple: [aa 55 73 05 01 00 00 38 83] corresponds à un niveau de glucose de 3,8 mmol/L
      if (dataString.substring(0, data.length - 6).includes('aa5573050100')) {
        const handleGlyc = (data: string) => {
          const n = data.search('aa5573050100')
          const tmpRead = parseInt(data.substring(n + 14, n + 16), 10)
          const valGlycemie = tmpRead / 10
          prop.emitData({
            label: Labels.GLYC,
            value: valGlycemie.toString(),
            unit: 'mmol/L',
          })
          prop.emitData({
            label: Labels.GLYC_2,
            value: (Math.round((valGlycemie / 18.18) * 10) / 10).toString(),
            unit: 'mg/dL',
          })
          prop.emitData({
            label: Labels.GLYC,
            value: Math.round(valGlycemie * 0.18).toString(),
            unit: 'g/L',
          })
        }
        handleGlyc(dataString)
        dataString = ''
      }
      // clean dataString buffer
      if (dataString.length > 200) {
        dataString = dataString.substring(dataString.length - 20)
      }
    }
  }

  handleSerial(
    prop.baudRate ? prop.baudRate : 9600,
    handlehexData,
    [
      {
        usbVendorId: 1659,
      },
    ],
    prop.onError,
    prop.onDisconnect,
    prop.onConnect
  )
}

function PM6100(prop: DeviceProps) {
  const PKG_HEADER = '55aa'
  const handlehexData = (data: string) => {
    const key = data.slice(0, 2)
    switch (key) {
      case '02': {
        const HandleECG = (data: string) => {
          const status = parseInt(data.slice(2, 4), 16)
          const HeartRate = parseInt(data.slice(4, 6), 16)
          if (status === 0) {
            prop.emitData({
              label: Labels.BPM,
              value: HeartRate.toString(),
              unit: 'bpm',
            })
          }
        }
        HandleECG(data)
        break
      }
      case '03': {
        const handleNIBP = (data: string) => {
          const status = parseInt(data.slice(2, 4), 16)
          const SysPressure = parseInt(data.slice(6, 8), 16)
          const DiaPressure = parseInt(data.slice(10, 12), 16)
          if (status === 0) {
            prop.emitData({
              label: Labels.NIBP,
              value: `${SysPressure}/${DiaPressure}`,
              unit: 'mmHg',
            })
          }
        }
        handleNIBP(data)
        break
      }

      case '04': {
        const handleSPO2 = (data: string) => {
          const status = parseInt(data.slice(2, 4), 16)
          const spo2Sat = parseInt(data.slice(4, 6), 16)
          const spo2Pr = parseInt(data.slice(6, 8), 16)
          if (status === 0) {
            prop.emitData({
              label: Labels.SPO2,
              value: `${spo2Sat}`,
              unit: '%',
            })
            prop.emitData({
              label: Labels.BPM,
              value: `${spo2Pr}`,
              unit: 'bpm',
            })
          }
        }
        handleSPO2(data)
        break
      }
      case '05': {
        const handleTemp = (data: string) => {
          const status = parseInt(data.slice(2, 4), 16)
          const temp_integral = parseInt(data.slice(4, 6), 16)
          const temp_decimal = parseInt(data.slice(6, 8), 16)
          if (status === 0) {
            prop.emitData({
              label: Labels.TEMP,
              value: `${temp_integral}.${temp_decimal}`,
              unit: '°C',
            })
          }
        }
        handleTemp(data)
        break
      }
    }
  }
  const handleheader = (data: string) => {
    for (let i = 0; i + 2 < data.length; i += 2) {
      const header = data.slice(i, i + PKG_HEADER.length)
      if (header === PKG_HEADER) {
        const pkg_start = i + PKG_HEADER.length
        // get the length of the pkg data
        const PKG_LENGTH = parseInt(data.slice(pkg_start, pkg_start + 2), 16)
        // get the pkg data
        const PKG_DATA = data.slice(pkg_start + 2, pkg_start + 2 + PKG_LENGTH * 2)
        // emit the data on the emitter as hexstring
        handlehexData(PKG_DATA)
      }
    }
    return ''
  }

  handleSerial(9600, handleheader, [], prop.onError, prop.onDisconnect, prop.onConnect)
}

function THERMO_TAIDOC(prop: DeviceProps) {
  const handleHexData = (data: string) => {
    // according to documentation
    // first 2 byte we need to ignore as it is header
    // then 2 byte are 0x26 and device current state
    // then next 2 byte are object temperature
    // then next 2 byte are ambient temperature
    if (data.slice(2, 4) === '26') {
      const reverseString = (str: string) => {
        const match = str.match(/.{1,2}/g)
        if (match === null) return ''
        return match.reverse().join('')
      }
      const objectTemp = parseInt(reverseString(data.slice(4, 8)), 16) / 10
      prop.emitData({
        label: Labels.TEMP,
        value: objectTemp.toString(),
        unit: '°C',
      })
    }
  }
  handleBluetooth(
    'TAIDOC TD1242',
    ['00001523-1212-efde-1523-785feabcd123'],
    ['00001524-1212-efde-1523-785feabcd123'],
    [Buffer.from([0x51, 0x26, 0x00, 0x00, 0x00, 0x00, 0xa3, 0x1a])],
    handleHexData,
    prop.onError,
    prop.onDisconnect,
    prop.onConnect
  )
}

/**
 * Blood glucose BLE meter
 * @param emitData emits biological tests so update bio tests
 */
function TAIDOC_BGMETER(prop: DeviceProps) {
  const handleHexData = (data: string) => {
    // according to documentation
    // first 2 byte we need to ignore as it is header
    // then 2 byte are 0x26 and device current state
    // then next 2 byte are the measure result
    // then next 2 byte are the type of measure
    if (data.slice(2, 4) === '26') {
      const reverseString = (str: string) => {
        const match = str.match(/.{1,2}/g)
        if (match === null) return ''
        return match.reverse().join('')
      }
      const hex2bin = (hex: string) => {
        return parseInt(hex, 16).toString(2).padStart(8, '0')
      }
      const roundToDecimal = (num: number) => {
        return Math.round(num * 100) / 100
      }

      const measure = parseInt(reverseString(data.slice(4, 8)), 16)

      const type = hex2bin(reverseString(data.slice(10, 12)))
      // type 1, type of measure
      // 0x0 = GEN
      // 0x1 = AC
      // 0x2 = PC
      // 0x3 = QC
      // const type1 = type.slice(0, 2)
      // type 2, type of measure
      // 0x0 = Gen
      // 0x6 = Hermatocrit
      // 0x7 = Ketone
      // 0x8 = Uric acid
      // 0x9 = Cholesterol
      // 0xB = HB
      // 0xC = Lactate
      // 0xD = Triglycerides
      const type2 = parseInt(type.slice(2, 6), 2).toString(16) //2
      //const type = parseInt(reverseString(data.slice(6, 8)), 16)
      switch (type2) {
        case '0':
          prop.emitData({
            label: Labels.GLYC,
            value: measure.toString(),
            unit: 'mg/dL',
          })
          // emit in mmol/L
          prop.emitData({
            label: Labels.GLYC,
            value: roundToDecimal(measure / 18).toString(),
            unit: 'mmol/L',
          })
          // backward compatibility?
          prop.emitData({
            label: Labels.GLYC_2,
            value: measure.toString(),
            unit: 'mg/dL',
          })
          // emit in mmol/L
          prop.emitData({
            label: Labels.GLYC_2,
            value: roundToDecimal(measure / 18).toString(),
            unit: 'mmol/L',
          })
          break
        case '6':
          prop.emitData({
            label: Labels.HEMATOCRITE,
            value: measure.toString(),
            unit: '%',
          })
          break
        case '7':
          prop.emitData({
            label: Labels.KETONE,
            value: roundToDecimal(measure / 30).toString(),
            unit: 'mmol/L',
          })
          break
        case '8':
          prop.emitData({
            label: Labels.URIC_ACID,
            value: roundToDecimal(measure / 10).toString(),
            unit: 'mg/dL',
          })
          break
        case '9':
          prop.emitData({
            label: Labels.CHOLESTEROL,
            value: measure.toString(),
            unit: 'mg/dL',
          })
          // emit in mmol/L
          prop.emitData({
            label: Labels.CHOLESTEROL,
            value: roundToDecimal(measure / 38.665).toString(),
            unit: 'mmol/L',
          })
          break
        case 'B':
          prop.emitData({
            label: Labels.HEMOGLOBINE,
            value: roundToDecimal(measure / 10).toString(),
            unit: 'g/dL',
          })
          break
        case 'C':
          prop.emitData({
            label: Labels.LACTATE,
            value: roundToDecimal(measure / 9.008).toString(),
            unit: 'mmol/L',
          })
          break
        case 'D':
          prop.emitData({
            label: Labels.TRYGLICERIDE,
            value: measure.toString(),
            unit: 'mg/dL',
          })
          // emit in mmol/L
          prop.emitData({
            label: Labels.TRYGLICERIDE,
            value: roundToDecimal(measure / 88.574).toString(),
            unit: 'mmol/L',
          })
          break
      }
    }
  }
  handleBluetooth(
    'TAIDOC TD4216 C432',
    ['00001523-1212-efde-1523-785feabcd123'],
    ['00001524-1212-efde-1523-785feabcd123'],
    [Buffer.from([0x51, 0x26, 0x00, 0x00, 0x00, 0x00, 0xa3, 0x1a])],
    handleHexData,
    prop.onError,
    prop.onDisconnect,
    prop.onConnect
  )
}

function F4(prop: DeviceProps) {
  const handleHexData = (data: string) => {
    const kg = data.slice(5, 9)
    const intKG = parseInt(kg, 16) / 1000
    if (intKG === 0) return
    prop.emitData({
      label: Labels.WEIGHT,
      value: intKG.toString(),
      unit: 'kg',
    })
  }
  // TODO : add svc uuid when we receive new connected scales
  handleBluetooth('F4', [], [], [], handleHexData, prop.onError, prop.onDisconnect, prop.onConnect)
}
